import { useEffect } from "react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import styles from "./AdminAuthGuard.module.css";

/** Returns the main app URL. Expects VITE_APP_URL to be set in all environments;
 *  the origin-based fallback only works when admin runs on an `admin.` subdomain. */
function getAppUrl(): string {
  return import.meta.env.VITE_APP_URL || window.location.origin.replace("admin.", "");
}

function getSignInUrl(): string {
  const returnUrl = encodeURIComponent(window.location.href);
  return `${getAppUrl()}/login?returnUrl=${returnUrl}`;
}

function getDashboardUrl(): string {
  return `${getAppUrl()}/dashboard`;
}

export function AdminAuthGuard({ children }: { children: React.ReactNode }) {
  const { status } = useAdminAuth();

  useEffect(() => {
    if (status === "unauthenticated") {
      window.location.replace(getSignInUrl());
    }
  }, [status]);

  if (status === "loading" || status === "unauthenticated") {
    return (
      <div className={styles.container}>
        <div className={styles.spinner} />
        <p className={styles.text}>
          {status === "unauthenticated" ? "Redirecting to sign in…" : "Verifying access…"}
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.container}>
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h1 className={styles.title}>Unable to connect</h1>
        <p className={styles.text}>
          Could not reach the API server. Make sure the backend is running and try again.
        </p>
        <div className={styles.actions}>
          <button onClick={() => window.location.reload()} className={styles.link}>
            ↻ Retry
          </button>
        </div>
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <div className={styles.container}>
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
        <h1 className={styles.title}>Access denied</h1>
        <p className={styles.text}>
          Your account doesn't have administrator access. If you believe this is an error, contact
          the system administrator.
        </p>
        <div className={styles.actions}>
          <a href={getDashboardUrl()} className={styles.link}>
            ← Return to Dashboard
          </a>
          <a href={getSignInUrl()} className={styles.linkSecondary}>
            Sign in with a different account
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
